import "./EsimTransferFlow.scss";

import { QRCodeSVG } from "qrcode.react";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { SubscriptionCard } from "@/components/Layout/BaseLayouts/SubscriptionCard/SubscriptionCard";
import { EsimQrCode } from "@/components/Portal/components/EsimQrCode/EsimQrCode.tsx";
import { RegistrationDetails } from "@/components/Portal/DeviceManagement/Details/DetailsEsimDisplay/RegistrationDetails/RegistrationDetails";
import { SubscriptionInfoBox } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionInfoBox/SubscriptionInfoBox";
import { useIsMobile } from "@/hooks/useIsMobile";
import { parseEsimMatchingId } from "@/utils/esimActivationCode";
import {
  getEsimInstallUrl,
  tryNavigateToEsimInstall,
} from "@/utils/esimInstallUrl";
import { capitalizeFirstLetter } from "@/utils/translationHelpers";

/**
 * The three inline states an eSIM change (reload/transfer/physical→eSIM)
 * moves through once opened: confirm → waiting → QR ready. Same card the
 * whole time, no route change - see Details.tsx for why.
 */
export const EsimTransferFlow = ({
  dataTestid,
  cardTitle,
  confirmDescription,
  onConfirm,
  confirmDisabled,
  onClose,
  startedChange,
  changeInProgress,
  matchingId,
  waitingText,
  readyTitle,
  readyDescription,
  readyLink,
}: {
  dataTestid: string;
  cardTitle: string;
  confirmDescription: ReactNode;
  onConfirm: () => void;
  confirmDisabled?: boolean;
  onClose: () => void;
  startedChange: boolean;
  changeInProgress: boolean;
  matchingId?: string;
  waitingText: string;
  readyTitle: ReactNode;
  readyDescription: ReactNode;
  readyLink: ReactNode;
}) => {
  const { t } = useTranslation("portal");
  const { t: tDetails } = useTranslation(undefined, {
    keyPrefix: "cockpit.managementTile.details.sections.details",
  });
  const isMobile = useIsMobile();
  const [showRegistrationDetails, setShowRegistrationDetails] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);

  if (startedChange && !changeInProgress && matchingId) {
    const lpa = parseEsimMatchingId(matchingId)?.completeActivationCode;
    const canNativeInstall = !!lpa && !!getEsimInstallUrl(lpa);
    const showQr = !isMobile || showQrCode || !canNativeInstall;

    return (
      <SubscriptionCard
        dataTestid={dataTestid}
        stickyHeader
        className="!gap-4"
        title={cardTitle}
        action={
          <Button
            dataTestid={`${dataTestid}-done`}
            size="sm"
            variant="primary"
            onClick={onClose}
          >
            {capitalizeFirstLetter(t("common.buttons.done"))}
          </Button>
        }
      >
        {showQr ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4">
              <p className="text-body-base font-semibold leading-none text-foreground-primary">
                {readyTitle}
              </p>
              <p className="text-body-sm leading-none text-foreground-secondary">
                {readyDescription}
              </p>
            </div>
            <div className="flex justify-center">
              <div className="border-primary flex items-center rounded-md border p-2">
                <EsimQrCode matchingId={matchingId} size={72} />
              </div>
            </div>
            <div className="flex justify-center">{readyLink}</div>
            <div className="flex justify-center">
              <ShowCodeToggle
                dataTestid={`${dataTestid}-show-code-button`}
                expanded={showRegistrationDetails}
                hideLabel={tDetails("registrationDetails.hideCode")}
                showLabel={tDetails("registrationDetails.showCode")}
                onToggle={() =>
                  setShowRegistrationDetails(!showRegistrationDetails)
                }
              />
            </div>
            {showRegistrationDetails && (
              <RegistrationDetails matchingId={matchingId} />
            )}
          </div>
        ) : (
          <SubscriptionInfoBox className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <p className="text-body-sm font-semibold leading-none text-foreground-primary">
                {tDetails("installOnThisDevice.title")}
              </p>
              <p className="text-body-sm leading-none text-foreground-secondary">
                {tDetails("installOnThisDevice.text")}
              </p>
            </div>
            <Button
              dataTestid={`${dataTestid}-install-esim-button`}
              variant="primary"
              fullWidth
              onClick={() => {
                if (!tryNavigateToEsimInstall(matchingId)) {
                  setShowQrCode(true);
                }
              }}
            >
              {tDetails("installOnThisDevice.button")}
            </Button>
          </SubscriptionInfoBox>
        )}
      </SubscriptionCard>
    );
  }

  if (changeInProgress) {
    return (
      <SubscriptionCard
        dataTestid={dataTestid}
        stickyHeader
        title={cardTitle}
        className="!gap-4"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4">
            <p className="text-body-base font-semibold leading-none text-foreground-primary">
              {readyTitle}
            </p>
            <p className="text-body-sm leading-none text-foreground-secondary">
              {waitingText}
            </p>
          </div>
          <div className="flex justify-center">
            <div className="border-primary flex items-center rounded-md border p-2">
              <div className="pulsating-blur">
                <QRCodeSVG value="digitalrepublic.ch" size={72} />
              </div>
            </div>
          </div>
        </div>
      </SubscriptionCard>
    );
  }

  return (
    <SubscriptionCard
      dataTestid={dataTestid}
      stickyHeader
      className="!gap-4"
      title={cardTitle}
      action={
        <>
          <Button
            dataTestid={`${dataTestid}-cancel`}
            size="sm"
            variant="secondary"
            onClick={onClose}
          >
            {capitalizeFirstLetter(t("common.buttons.cancel"))}
          </Button>
          <Button
            dataTestid={`${dataTestid}-confirm`}
            size="sm"
            variant="primary"
            disabled={confirmDisabled}
            onClick={onConfirm}
          >
            {capitalizeFirstLetter(t("common.buttons.confirm"))}
          </Button>
        </>
      }
    >
      <div className="text-body-sm leading-none text-foreground-secondary">
        {confirmDescription}
      </div>
    </SubscriptionCard>
  );
};

const ShowCodeToggle = ({
  dataTestid,
  expanded,
  hideLabel,
  showLabel,
  onToggle,
}: {
  dataTestid: string;
  expanded: boolean;
  hideLabel: string;
  showLabel: string;
  onToggle: () => void;
}) => (
  <button
    data-testid={dataTestid}
    className="text-left text-body-xs font-semibold text-foreground-primary underline"
    type="button"
    aria-expanded={expanded}
    // Only advertise the id once RegistrationDetails is actually
    // mounted - it doesn't exist in the DOM while collapsed.
    aria-controls={expanded ? "registration-details" : undefined}
    onClick={onToggle}
  >
    {expanded ? hideLabel : showLabel}
  </button>
);
